import { useEffect, useMemo } from 'react';
import TagManager from 'react-gtm-module';

import Intercom from '@intercom/messenger-js-sdk';
import {
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	isRouteErrorResponse,
	useFetchers,
	useLoaderData,
	useLocation,
	useNavigation,
	useRevalidator,
	useRouteError,
} from '@remix-run/react';
import { createBrowserClient } from '@supabase/ssr';
import { json } from '@vercel/remix';
import type { LinksFunction, LoaderFunctionArgs } from '@vercel/remix';
import crypto from 'crypto';
import NProgress from 'nprogress';
import { ExternalScripts } from 'remix-utils/external-scripts';
import sonnerStyles from 'sonner/dist/styles.css?url';
import { useVitePostHog } from 'vite-plugin-posthog/react';
import { getOnboardingProgress } from '~/utils/onboarding.server';

import Toast from './components/Toast';
import { getSupabase } from './services/supabase.server';
import globalStyles from './styles/globals.css?url';
import styles from './styles/tailwind.css?url';
import { trackSocialPixels } from './utils/tracking';
import { getDetailedUserInfo } from './utils/user.server';

export const links: LinksFunction = () => {
	const links = [
		{ rel: 'stylesheet', href: styles },
		{ rel: 'stylesheet', href: globalStyles },
		{ rel: 'stylesheet', href: sonnerStyles },
		{ rel: 'alternate', hrefLang: 'en', href: 'https://pagepilot.ai' },
		{
			rel: 'apple-touch-icon',
			sizes: '180x180',
			href: '/apple-touch-icon.png',
		},
		{
			rel: 'icon',
			type: 'image/png',
			sizes: '32x32',
			href: '/favicon-32x32.png',
		},
		{
			rel: 'icon',
			type: 'image/png',
			sizes: '16x16',
			href: '/favicon-16x16.png',
		},
		{ rel: 'manifest', href: '/site.webmanifest' },
		{ rel: 'mask-icon', href: '/safari-pinned-tab.svg', color: '#5bbad5' },
	];

	return links;
};

export const meta = () => [
	{
		title: 'Create Shopify AI Product Pages & Stores in 2 Minutes | PagePilot.ai',
	},
	{
		charset: 'utf-8',
	},
	{
		name: 'viewport',
		content: 'width=device-width, initial-scale=1.0',
	},
	{
		name: 'msapplication-TileColor',
		content: '#fff',
	},
	{
		name: 'theme-color',
		content: '#ffffff',
	},
	{
		name: 'keywords',
		content:
			'product description generator,shopify landing page generator,shopify ai description,shopify product page generator,ai product page generator,instant landing page creation,high-quality landing page builder,product description for shopify,easy-to-use landing page generator,quick product page creation,automated product description tool,dropshipping copy generator,fast landing page builder for dropshipping,free product description generator,ai product description,shopify description maker,text generator for product descriptions,automated copywriting for dropshipping,product copy generator,landing page generator for shopify,dropshipping copyright,product page copy generator for shopify,shopify product description tool,user-friendly product copy generator,high-converting landing page builder,copywriting tool for online shops,easy-to-use product description generator,shopify dropshipping copy generator,landing page creation tool for online shops,product page generator for shopify stores,ai-powered product description generator,optimized copy and images for dropshipping,shopify landing page builder,user-oriented copywriting tool,e-commerce copy generator,dynamic product description tool,conversion-focused landing page builder,ai-driven copywriting for dropshipping,product page optimization for shopify,customizable landing page templates,dropshipping copy and image optimization,dropshipping copyright tool,shopify copyright ai,product copyright shopify,shopify store copy generator,streamlined product description creation,automated copywriting for online shops,enhanced landing page performance,seo-friendly product page generator,persuasive copywriting for dropshippers,shopify landing page optimization,user-centric product copy generator,effortless creation of product descriptions,targeted copywriting for e-commerce,seamless integration with shopify,conversion-optimized landing page templates,comprehensive copy and image solution for dropshippers,easy shopify listing tool,shopify product list ai,dropshipping product listing tool,free dropshipping product listing,list shopify product fast,import shopify products,shopify product description maker,easy product description maker,product description writer,easy product listing,shopify product generator,ai shopify description',
	},
	{
		name: 'description',
		content:
			'Create high converting product descriptions and landing pages effortlessly with PagePilot AI - the ultimate free AI tool for easy product listing. Boost your conversions with high converting copy. Listing products on Shopify easy with the power of AI. Powered by AI for Shopify Dropshipping Community.',
	},
	{
		name: 'og:title',
		content: 'Create Shopify AI Product Pages & Stores in 2 Minutes | PagePilot.ai',
	},
	{
		name: 'og:description',
		content:
			'Create high converting product descriptions and landing pages effortlessly with PagePilot AI - the ultimate free AI tool for easy product listing. Boost your conversions with high converting copy. Listing products on Shopify easy with the power of AI. Powered by AI for Shopify Dropshipping Community.',
	},
	{
		name: 'og:image',
		content: 'https://pagepilot.ai/images/metaImage.jpg',
	},
	{
		name: 'og:url',
		content: 'https://pagepilot.ai',
	},
];

export const loader = async ({ request }: LoaderFunctionArgs) => {
	try {
		// Set the env variables for the client
		const env = {
			SUPABASE_URL: process.env.SUPABASE_URL || '',
			SUPABASE_ANON_KEY: process.env.SUPABASE_ANON_KEY || '',
		};

		// Get the supabase client and headers
		const { supabase, headers } = getSupabase(request);

		const {
			data: { user },
		} = await supabase.auth.getUser();

		let userDetails = null;
		let onboardingProgress = null;
		let intercomHash = '';

		if (user) {
			userDetails = await getDetailedUserInfo(supabase);
			onboardingProgress = await getOnboardingProgress(supabase, user);
			intercomHash = crypto
				.createHmac('sha256', 'cfchpgkmjueogQCkDCAIDIAFhFI00A9qIPTDqCv-')
				.update(user.id)
				.digest('hex');
		}

		return json(
			{
				env,
				userDetails: userDetails || null,
				onboardingProgress: onboardingProgress || null,
				intercomHash: intercomHash || null,
			},
			{
				headers,
			},
		);
	} catch (error) {
		console.error('Loader error:', error);
		// Return a default response instead of throwing
		return json({
			env: {
				SUPABASE_URL: process.env.SUPABASE_URL || '',
				SUPABASE_ANON_KEY: process.env.SUPABASE_ANON_KEY || '',
			},
			userDetails: null,
			onboardingProgress: null,
			intercomHash: null,
		});
	}
};

export default function App() {
	const posthog = useVitePostHog();
	const { env, userDetails, onboardingProgress, intercomHash } = useLoaderData<typeof loader>();
	const navigation = useNavigation();
	const location = useLocation();
	const fetchers = useFetchers();
	const revalidator = useRevalidator();
	const canonicalUrl = `https://pagepilot.ai${location.pathname}`;

	useEffect(() => {
		const hashParams = new URLSearchParams(window.location.hash.slice(1));
		const accessToken = hashParams.get('access_token');
		const refreshToken = hashParams.get('refresh_token');

		if (accessToken && refreshToken) {
			const supabase = createBrowserClient(env.SUPABASE_URL, env.SUPABASE_ANON_KEY);
			supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken }).then(({ data, error }) => {
				window.history.replaceState({}, document.title, '/list');
				window.location.reload();
			});
		}
	}, []);

	useEffect(() => {
		trackSocialPixels();
	}, []);

	const state = useMemo<'idle' | 'loading'>(
		function getGlobalState() {
			const states = [navigation.state, ...fetchers.map(fetcher => fetcher.state)];
			if (states.every(state => state === 'idle')) return 'idle';
			return 'loading';
		},
		[navigation.state, fetchers],
	);

	useEffect(() => {
		if (state === 'loading') NProgress.start();
		if (state === 'idle') NProgress.done();
	}, [navigation.state, state]);

	useEffect(() => {
		const tagManagerArgs = {
			gtmId: 'GTM-MTGJJVG',
		};

		TagManager.initialize(tagManagerArgs);
	}, []);

	useEffect(() => {
		posthog?.capture('$pageview');
	}, [location, posthog]);

	useEffect(() => {
		if (userDetails?.user_id && intercomHash && window.Intercom) {
			window.Intercom('boot', {
				api_base: 'https://api-iam.intercom.io',
				app_id: 'eyh852hw',
				user_id: userDetails.user_id || '',
				user_hash: intercomHash,
			});
		}
	}, [userDetails?.user_id, intercomHash]);

	useEffect(() => {
		if (location.pathname.includes('/product/edit/') || location.pathname.includes('/preview/')) {
			return;
		}

		let createdAt_timestamp;
		if (userDetails?.user_id && userDetails?.created_at) {
			const createdAtDate = new Date(userDetails.created_at);
			createdAt_timestamp = Math.floor(createdAtDate.getTime() / 1000);
		}

		if (userDetails?.user_id) {
			Intercom({
				app_id: 'eyh852hw',
				user_id: userDetails.user_id || '',
				name: userDetails?.first_name || '',
				email: userDetails?.email || '',
				created_at: createdAt_timestamp,
			});
		} else {
			Intercom({
				app_id: 'eyh852hw',
			});
		}
	}, [userDetails, location.pathname]);

	useEffect(() => {
		if (location.pathname === '/success') {
			revalidator.revalidate();
		}
	}, [location.pathname, revalidator]);

	useEffect(() => {
		// This is to handle the referral URL from Rewardful
		try {
			const referral_url = new URL(window?.location?.href);

			if (referral_url?.searchParams?.has('gclid') && referral_url?.searchParams?.has('via')) {
				setTimeout(() => {
					document.cookie =
						'rewardful.referral=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' + window.location.hostname;
				}, 2000);
			}
		} catch (error) {
			posthog?.capture('rewardful_page_error', {
				error: error?.message || 'Error handling referral URL from Rewardful',
			});
		}
	}, []);

	return (
		<html lang="en" className="h-full">
			<head>
				<meta name="test" content="test-salamX" />
				<script
					async
					dangerouslySetInnerHTML={{
						__html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-MTGJJVG');`,
					}}></script>
				<script
					async
					dangerouslySetInnerHTML={{
						__html: `!function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys onSessionId".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);posthog.init('phc_W4R4LBICy8rJgdBsLJsWl3TO6RtnzK3ECEcRhLOey9B',{api_host:'https://eu.posthog.com',autocapture:false,capture_pageview:true,capture_pageleave:false,disable_session_recording:true});`,
					}}></script>
				<script
					defer
					dangerouslySetInnerHTML={{
						__html: `(function(w,r){w._rwq=r;w[r]=w[r]||function(){(w[r].q=w[r].q||[]).push(arguments)}})(window,'rewardful');`,
					}}></script>
				<script async src="https://r.wdfl.co/rw.js" data-rewardful="9b26e1"></script>
				<Meta />
				<Links />
				<link rel="canonical" href={canonicalUrl} />
				{location.pathname === '/' && (
					<>
						<script
							rel="script"
							type="application/ld+json"
							key="organization-schema"
							dangerouslySetInnerHTML={{
								__html: JSON.stringify({
									'@context': 'https://schema.org',
									'@type': 'OnlineStore',
									name: 'PagePilot.ai',
									url: 'https://pagepilot.ai',
									logo: 'https://pagepilot.ai/images/logo/logo_black.png',
									description:
										'PagePilot.ai is an AI-powered tool that helps create high-converting product descriptions and landing pages for Shopify stores. It enables easy product listing and boosts conversions with AI-generated copy, specifically designed for the Shopify Dropshipping Community.',
									email: 'info@pagepilot.ai',
									sameAs: [],
									contactPoint: {
										'@type': 'ContactPoint',
										contactType: 'Customer Service',
										email: 'info@pagepilot.ai',
									},
									hasMerchantReturnPolicy: {
										'@type': 'MerchantReturnPolicy',
										returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
										merchantReturnDays: 14,
										returnMethod: 'https://schema.org/ReturnByDigitalMeans',
										returnFees: 'https://schema.org/FreeReturn',
										refundType: 'https://schema.org/FullRefund',
									},
								}),
							}}
						/>

						<script
							type="application/ld+json"
							dangerouslySetInnerHTML={{
								__html: JSON.stringify({
									'@context': 'https://schema.org',
									'@type': 'FAQPage',
									mainEntity: [
										{
											'@type': 'Question',
											name: 'What is PagePilot.ai used for?',
											acceptedAnswer: {
												'@type': 'Answer',
												text: 'PagePilot.ai is a tool that helps you launch and test e-commerce products faster. You just need to copy and paste the Aliexpress URL or the product link directly from another Shopify store and we build a unique and high converting landing page in just a few minutes, using AI. More on that, we help you generate the Ads Creatives easily and every day we give you 10 different and unique winning products to test.',
											},
										},
										{
											'@type': 'Question',
											name: 'Is the subscription commitment-free?',
											acceptedAnswer: {
												'@type': 'Answer',
												text: 'Absolutely! You can try PagePilot.ai for free. No commitment, no credit card required!',
											},
										},
										{
											'@type': 'Question',
											name: 'How can I import to Shopify the generated product page?',
											acceptedAnswer: {
												'@type': 'Answer',
												text: 'You will need to connect your Shopify Store to PagePilot. Once you have done that, you can import the product page to your Shopify store with just one click. Easy peasy!',
											},
										},
										{
											'@type': 'Question',
											name: 'What services does PagePilot.ai offer?',
											acceptedAnswer: {
												'@type': 'Answer',
												text: 'Product Page generation (from Aliexpress or Shopify link), Ad Creatives, and 10 Daily Winning Products to test.',
											},
										},
										{
											'@type': 'Question',
											name: 'How many languages does PagePilot.ai support?',
											acceptedAnswer: {
												'@type': 'Answer',
												text: 'You can generate the product page in 30+ languages. If your language is not there, please contact us and we will add it for you.',
											},
										},
										{
											'@type': 'Question',
											name: 'Can I create products during the free period and link them to my store?',
											acceptedAnswer: {
												'@type': 'Answer',
												text: 'You can create up to 3 product pages on your free plan. In order to import them in Shopify, you need a paid plan.',
											},
										},
										{
											'@type': 'Question',
											name: 'After I cancel my subscription, will the products I created with PagePilot.ai be deleted? What happens to the products I imported into my Shopify store?',
											acceptedAnswer: {
												'@type': 'Answer',
												text: 'If you decide to cancel your subscription, the products you created with PagePilot.ai will not be deleted; however, you will lose the ability to create new products using PagePilot. The products you imported into your Shopify store will remain unaffected.',
											},
										},
										{
											'@type': 'Question',
											name: 'How do I cancel my subscription?',
											acceptedAnswer: {
												'@type': 'Answer',
												text: "Simply log in to the platform, click on the Billing from menu and open the customer portal. Even after canceling your subscription, PagePilot.ai remains available to you until the end of your current period, whether it's monthly or annually.",
											},
										},
									],
								}),
							}}
						/>
					</>
				)}
			</head>
			<body className="flex h-full w-full flex-col font-inter">
				<noscript>
					<iframe
						title="Google Tag Manager"
						src="https://www.googletagmanager.com/ns.html?id=GTM-MTGJJVG"
						height="0"
						width="0"
						style={{ display: 'none', visibility: 'hidden' }}></iframe>
				</noscript>
				<Outlet
					context={{
						env,
						userDetails,
						onboardingProgress,
					}}
				/>
				<ScrollRestoration />
				<ExternalScripts />
				<Scripts />
				<Toast />

				{location.pathname === '/' && (
					<script type="text/javascript" src="//static.klaviyo.com/onsite/js/klaviyo.js?company_id=S7nYJg"></script>
				)}
				{location.pathname === '/' && (
					<script
						id="cookieyes"
						type="text/javascript"
						src="https://cdn-cookieyes.com/client_data/e6512becdcad7980704e15b3/script.js"></script>
				)}
				<script
					defer
					dangerouslySetInnerHTML={{
						__html: `(function(e,t,o,n,p,r,i){e.visitorGlobalObjectAlias=n;e[e.visitorGlobalObjectAlias]=e[e.visitorGlobalObjectAlias]||function(){(e[e.visitorGlobalObjectAlias].q=e[e.visitorGlobalObjectAlias].q||[]).push(arguments)};e[e.visitorGlobalObjectAlias].l=(new Date).getTime();r=t.createElement("script");r.src=o;r.async=true;i=t.getElementsByTagName("script")[0];i.parentNode.insertBefore(r,i)})(window,document,"https://diffuser-cdn.app-us1.com/diffuser/diffuser.js","vgo");
							vgo('setAccount', '1002943738');
							vgo('setTrackByDefault', true);
							vgo('process');`,
					}}></script>
			</body>
		</html>
	);
}

// export default withSentry(App);

export const ErrorBoundary = () => {
	const error: any = useRouteError();
	const posthog = useVitePostHog();

	// Only capture non-hydration related errors
	if (
		error &&
		!error?.message?.includes('Hydration failed') &&
		!error?.message?.includes('Minified React error #418') &&
		!error?.message?.includes('Text content does not match server-rendered HTML')
	) {
		posthog?.capture('page_error', {
			error: error.message,
		});
	}

	// For hydration errors, just return null to let React retry
	if (
		error?.message?.includes('Hydration failed') ||
		error?.message?.includes('Minified React error #418') ||
		error?.message?.includes('Text content does not match server-rendered HTML')
	) {
		return null;
	}

	if (isRouteErrorResponse(error) && error.status === 404) {
		return (
			<html className="h-full">
				<head>
					<title>Oops!</title>
					<Meta />
					<Links />
				</head>
				<body className="h-full">
					<div className="flex h-full w-full flex-col items-center justify-center gap-6 p-6">
						<img className="w-full max-w-[300px] lg:max-w-[400px]" src="/images/error-404.png" alt="Error" />

						<h2 className="text-center text-4xl font-semibold">404: The page you are looking for isn't here</h2>
						<p className="text-center text-black/60">
							You either tried some shady route or you came here by mistake. Whichever it is, try using the navigation.
						</p>
						<p className="text-center text-black/60">Get in touch with us on Discord if the problem persists.</p>

						<Link to={'/list'} rel="noreferrer" className="btn-primary">
							Back To Home
						</Link>
					</div>

					<Scripts />
				</body>
			</html>
		);
	}
	// when true, this is what used to go to `CatchBoundary`
	if (isRouteErrorResponse(error)) {
		return (
			<div>
				<h1>Oops</h1>
				<p>Status: {error.status}</p>
				<p>{error.data.message}</p>
			</div>
		);
	}

	return (
		<html className="h-full">
			<head>
				<title>Oops!</title>
				<Meta />
				<Links />

				<script
					async
					dangerouslySetInnerHTML={{
						__html: `
    !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys onSessionId".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
    posthog.init('phc_W4R4LBICy8rJgdBsLJsWl3TO6RtnzK3ECEcRhLOey9B', { api_host: 'https://eu.posthog.com', autocapture: false, capture_pageview: true, capture_pageleave: false, disable_session_recording: true })`,
					}}></script>
			</head>
			<body className="h-full">
				<div className="flex h-full w-full flex-col lg:flex-row">
					<div className="mx-auto flex w-full max-w-[1000px] flex-col items-center justify-center gap-6 p-6">
						<img className="w-full max-w-[350px] lg:max-w-[400px]" src="/images/error.png" alt="Error" />

						<h2 className="text-center text-4xl font-semibold">Oopsie! Let's Fix This Together</h2>
						<p className="text-center text-lg text-black/60">
							Looks like we hit a snag, but no worries! Give the page a quick refresh, and you should be all set.
						</p>
						<p className="rounded-lg bg-gray-100/80 p-2 text-center text-lg text-black/60">
							If this keeps happening, our wizard behind the curtain, Alex, is ready to help you out on Discord or via
							email. Reach out anytime at{' '}
							<a href="mailto:alex@pagepilot.ai" className="font-medium text-blueSecondary">
								alex@pagepilot.ai
							</a>{' '}
							and we'll get you back on track in no time!
						</p>

						<div className="flex gap-6">
							<button
								onClick={() => {
									window.location.reload();
								}}
								className="btn-primary">
								Refresh Page
							</button>
							<Link to={'/'} rel="noreferrer" className="btn-secondary">
								Go To Dashboard
							</Link>
						</div>
					</div>
				</div>

				<Scripts />
			</body>
		</html>
	);
};
